// §4.2 — the kernel event bus.
//
// A typed synchronous emitter over a **fixed** event union. No service imports another
// service's module (§4.2); a cross-service reaction goes through here instead, which is what
// keeps `wm` testable without `vfs` and keeps the kernel graph acyclic.
import type { FsEvent, Rect, SkinId, Theme, VfsPath, WinState } from '@hangar/shared';

/**
 * The closed event union. Adding a member here is a deliberate act — an event that is not in
 * this table cannot be emitted or subscribed to, and `tests/unit/bus.test.ts` has a
 * compile-time fixture asserting exactly that.
 */
export interface BusEvents {
  // ── window:* ────────────────────────────────────────────────────────────────────────────
  'window:created': { id: string; appId: string; instanceKey: string };
  'window:closed': { id: string; appId: string; instanceKey: string };
  'window:focused': { id: string | null };
  'window:moved': { id: string; rect: Rect };
  'window:stateChanged': { id: string; state: WinState };
  'window:desktopChanged': { id: string; desktopId: string };
  'window:chromeChanged': { id: string };
  'window:desktopsChanged': { activeDesktopId: string };

  // ── fs:* ────────────────────────────────────────────────────────────────────────────────
  'fs:changed': FsEvent;
  'fs:quotaWarning': { usedBytes: number; quotaBytes: number };
  /** §18 F4 — a write that actually failed for want of room, not the 90 % warning. */
  'fs:quotaExceeded': { path: string };
  'fs:syncStateChanged': { online: boolean; pending: number };
  'fs:conflict': { path: VfsPath; copyPath: VfsPath };

  // ── app:* ───────────────────────────────────────────────────────────────────────────────
  'app:installed': { appId: string };
  'app:uninstalled': { appId: string };
  'app:launched': { appId: string; winId: string };
  'app:healthChanged': { appId: string; healthy: boolean };
  'app:menuPublished': { winId: string };
  /** §9.6 — the installed set changed: installed, uninstalled, or a health flip. */
  'app:registryChanged': Record<string, never>;
  /**
   * §9.6 / §18 F10 — a `resumable` app's frame was unloaded after 15 minutes minimized. The
   * *window* is untouched: what goes is the iframe, which is the memory the unload reclaims.
   */
  'app:unloaded': { instanceKey: string; appId: string };
  /** §9.6 — an unloaded window is wanted again; the host rebuilds it from its session blob. */
  'app:resumed': { instanceKey: string; appId: string };

  // ── bridge:* (§9.3) ─────────────────────────────────────────────────────────────────────
  'bridge:message': { instanceKey: string; key: string };
  'bridge:drop': { reason: string };
  /** A valid message on a channel no milestone has routed yet — counted, never answered. */
  'bridge:unrouted': { instanceKey: string; key: string };

  // ── notify:* ────────────────────────────────────────────────────────────────────────────
  'notify:posted': { id: string; appId: string };
  'notify:dismissed': { id: string };
  'notify:action': { id: string; action: string };
  'notify:cleared': { appId: string | null };

  // ── keys:* (§6.4, §9.7) ─────────────────────────────────────────────────────────────────
  /**
   * The active keymap changed, so **the set of chords the shell claims** may have too — by a skin
   * change or by a §11.6 per-binding override. `settings:skinChanged` is not a substitute: it
   * fires while the old keymap is still loaded (the new one arrives from a React effect), and it
   * says nothing at all about an override.
   */
  'keys:reservedChanged': { reservedChords: string[] };

  // ── settings:* ──────────────────────────────────────────────────────────────────────────
  'settings:changed': { key: string; value: unknown };
  'settings:skinChanged': { skin: SkinId };
  'settings:themeChanged': { theme: Theme };

  // ── shell:* ─────────────────────────────────────────────────────────────────────────────
  /** §6.4 — the switcher chord pressed again while the switcher is already open (M6.5). */
  'shell:switcherAdvance': { delta: number };

  // ── session:* ───────────────────────────────────────────────────────────────────────────
  'session:dirty': Record<string, never>;
  'session:saved': Record<string, never>;
  'session:restored': { windows: number };
}

export type BusEvent = keyof BusEvents;

export type BusHandler<K extends BusEvent> = (payload: BusEvents[K]) => void;

/** Unsubscribe. Calling it twice is a no-op. */
export type Unsubscribe = () => void;

export interface Bus {
  on<K extends BusEvent>(event: K, handler: BusHandler<K>): Unsubscribe;
  once<K extends BusEvent>(event: K, handler: BusHandler<K>): Unsubscribe;
  emit<K extends BusEvent>(event: K, payload: BusEvents[K]): void;
  /** Live subscriber count for one event — used by tests and App Inspector. */
  listenerCount(event: BusEvent): number;
}

export function createBus(): Bus {
  const handlers = new Map<BusEvent, Set<BusHandler<BusEvent>>>();

  const on = <K extends BusEvent>(event: K, handler: BusHandler<K>): Unsubscribe => {
    const set = handlers.get(event) ?? new Set<BusHandler<BusEvent>>();
    // The map is heterogeneous by construction; the public signature keeps callers honest.
    const erased = handler as BusHandler<BusEvent>;
    set.add(erased);
    handlers.set(event, set);
    let live = true;
    return () => {
      if (!live) return;
      live = false;
      set.delete(erased);
    };
  };

  return {
    on,

    once(event, handler) {
      const off = on(event, ((payload) => {
        off();
        (handler as BusHandler<BusEvent>)(payload);
      }) as BusHandler<typeof event>);
      return off;
    },

    emit(event, payload) {
      const set = handlers.get(event);
      if (!set || set.size === 0) return;
      // Copy first: a handler may subscribe or unsubscribe during dispatch.
      for (const handler of [...set]) {
        if (set.has(handler)) handler(payload);
      }
    },

    listenerCount(event) {
      return handlers.get(event)?.size ?? 0;
    },
  };
}
